import React from "react"
import { Stack, TextInput, Select, Switch } from "@mantine/core"
import { DatePickerInputProps, DatePickerInput as DatePickerInputWithoutPlaceholder } from "@mantine/dates"

import classes from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/QuestionCreateUpdateForm/QuestionCreateUpdateForm.module.css"
import { Icon } from "@/components"
import { useForm } from "@/pages/hooks/useForm"
import { QuestionForm } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/CreateQuestionModal"
import { OptionsAdder } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/OptionsAdder"
import { BooleanValue } from "@/pages/campaign-form/components/campaigns/personalizedMessages/components/CustomQuestions/BooleanValue"

const { TagType } = DjangIO.app.person.types

export const DatePickerInput = DatePickerInputWithoutPlaceholder as React.FC<
    DatePickerInputProps & { placeholder?: string }
>

export const questionTypes = [
    { value: TagType.string.value, label: "Text" },
    { value: TagType.number.value, label: "Number" },
    { value: TagType.date.value, label: "Date" },
    { value: TagType.boolean.value, label: "Yes/No" },
    { value: TagType.single_option_list.value, label: "Single selection" },
    { value: TagType.multi_options_list.value, label: "Multiple selection" },
] as const

export const stringifyedQuestionTypes = questionTypes.map((type) => ({ ...type, value: type.value.toString() }))

export const TYPES_WITH_DEFAULT_TEXT_INPUT = [TagType.string.value, TagType.number.value] as number[]

export interface DefaultValueComponentProps {
    questionForm: ReturnType<typeof useForm<QuestionForm>>
    isMulti?: boolean
    tagType: (typeof questionTypes)[number]["value"]
}

export const DefaultValueComponent = (props: DefaultValueComponentProps) => {
    if (props.tagType === TagType.date.value) {
        function parseLocalDate(dateString) {
            const [year, month, day] = dateString.split("-").map(Number)
            return new Date(year, month - 1, day) // Month is zero-indexed
        }
        return (
            <DatePickerInput
                placeholder="Default Values (Optional)"
                clearable
                popoverProps={{ classNames: { dropdown: classes.datePickerDropdown } }}
                value={
                    props.questionForm.values.default_string_value
                        ? parseLocalDate(props.questionForm.values.default_string_value)
                        : null
                }
                onChange={(value) =>
                    props.questionForm.setFieldValue(
                        "default_string_value",
                        value ? value.toLocaleDateString("en-CA") : null,
                    )
                }
                classNames={{ wrapper: classes.defaultInput }}
            />
        )
    }
    if (props.tagType === TagType.multi_options_list.value) {
        return <OptionsAdder isMulti questionForm={props.questionForm} />
    }
    if (props.tagType === TagType.single_option_list.value) {
        return <OptionsAdder questionForm={props.questionForm} />
    }

    if (props.tagType === TagType.boolean.value) {
        return <BooleanValue questionForm={props.questionForm} />
    }

    if (TYPES_WITH_DEFAULT_TEXT_INPUT.includes(props.tagType)) {
        return (
            <TextInput
                placeholder="Default value (optional)"
                value={props.questionForm.values.default_string_value}
                onChange={(e) => props.questionForm.setFieldValue("default_string_value", e.target.value)}
                type={props.questionForm.values.tag_type === TagType.number.value ? "number" : "text"}
                classNames={{
                    wrapper: classes.defaultInput,
                }}
            />
        )
    }

    return null
}

export const QuestionCreateUpdateForm = ({
    form,
    isEditing = false,
}: {
    form: ReturnType<typeof useForm<QuestionForm>>
    isEditing?: boolean
}) => {
    return (
        <Stack spacing="md">
            <TextInput
                label="Label"
                name="name"
                value={form.values.name}
                onChange={(e) => form.setFieldValue("name", e.target.value)}
                error={form.errors.name}
            />
            <Select
                name="tag_type"
                label="Type"
                disabled={isEditing}
                data={stringifyedQuestionTypes}
                value={form.values.tag_type.toString()}
                onChange={(value) => form.setFieldValue("tag_type", Number(value) as typeof form.values.tag_type)}
                error={form.errors.tag_type}
            />
            <div>
                <DefaultValueComponent questionForm={form} tagType={form.values.tag_type} />
            </div>
            <Switch
                name="add_as_custom_field"
                label="Add to contact record as a custom field"
                checked={form.values.add_as_custom_field}
                error={form.errors.add_as_custom_field}
                onChange={(e) => form.setFieldValue("add_as_custom_field", e.target.checked)}
            />
            <Switch
                name="required"
                label="Required"
                checked={form.values.required}
                onChange={(e) => form.setFieldValue("required", e.target.checked)}
                error={form.errors.required}
            />
            <div className={classes.infoContainer}>
                <Icon icon="info-circle" className={classes.icon} />
                <span className={classes.infoText}>New custom questions can be reused in other campaigns</span>
            </div>
        </Stack>
    )
}
